import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
    ArrowLeft, 
    Calendar, 
    FileAudio, 
    ExternalLink, 
    Upload,
    Loader2,
    Music,
    CheckCircle2,
    X,
    CloudUpload
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

// Max file size: 500MB
const MAX_FILE_SIZE = 500 * 1024 * 1024;

export default function OpportunityDetailPage() {
    const { syncId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    
    const [opportunity, setOpportunity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState(''); // 'preparing', 'uploading', 'confirming'
    
    const [trackTitle, setTrackTitle] = useState('');
    const [notes, setNotes] = useState('');
    const [audioFile, setAudioFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchOpportunity();
    }, [syncId]);

    const fetchOpportunity = async () => {
        try {
            const response = await fetch(`${API}/opportunities/${syncId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setOpportunity(data);
            } else {
                toast.error('Opportunity not found');
                navigate('/opportunities');
            }
        } catch (error) {
            toast.error('Error loading opportunity');
        } finally {
            setLoading(false);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (file) => {
        const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-wav', 'audio/aiff', 'audio/x-aiff'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload an audio file (MP3, WAV, AIFF)');
            return;
        }
        
        if (file.size > MAX_FILE_SIZE) {
            toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
            return;
        }
        
        setAudioFile(file);
        if (!trackTitle) {
            setTrackTitle(file.name.replace(/\.[^/.]+$/, ''));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!audioFile) {
            toast.error('Please upload an audio file');
            return;
        }

        setSubmitting(true);
        setUploadProgress(0);
        
        try {
            // Step 1: Get presigned upload URL
            setUploadStatus('preparing');
            const presignedResponse = await fetch(`${API}/uploads/presigned`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    sync_id: syncId,
                    file_name: audioFile.name,
                    content_type: audioFile.type,
                    file_size: audioFile.size
                })
            });

            if (!presignedResponse.ok) {
                const error = await presignedResponse.json();
                throw new Error(error.detail || 'Failed to prepare upload');
            }

            const { upload_url, submission_id, s3_key } = await presignedResponse.json();
            setUploadProgress(10);
            
            // Step 2: Upload directly to S3
            setUploadStatus('uploading');
            
            const xhr = new XMLHttpRequest();
            
            await new Promise((resolve, reject) => {
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        // Progress from 10% to 90% during upload
                        const percentComplete = 10 + (event.loaded / event.total) * 80;
                        setUploadProgress(Math.round(percentComplete));
                    }
                });
                
                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                    } else {
                        reject(new Error('Upload to storage failed'));
                    }
                });
                
                xhr.addEventListener('error', () => reject(new Error('Upload failed')));
                xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
                
                xhr.open('PUT', upload_url);
                xhr.setRequestHeader('Content-Type', audioFile.type);
                xhr.send(audioFile);
            });
            
            setUploadProgress(90);
            
            // Step 3: Confirm upload with backend
            setUploadStatus('confirming');
            const confirmResponse = await fetch(`${API}/uploads/confirm`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    submission_id,
                    sync_id: syncId,
                    s3_key,
                    track_title: trackTitle,
                    notes,
                    original_file_name: audioFile.name,
                    content_type: audioFile.type,
                    file_size: audioFile.size
                })
            });

            if (!confirmResponse.ok) {
                const error = await confirmResponse.json();
                throw new Error(error.detail || 'Failed to confirm upload');
            }

            setUploadProgress(100);
            setSubmitted(true);
            toast.success('Submission created successfully!');
            
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Error submitting track');
        } finally {
            setSubmitting(false);
            setUploadStatus('');
        }
    };

    const getUploadStatusText = () => {
        switch (uploadStatus) {
            case 'preparing':
                return 'Preparing upload...';
            case 'uploading':
                return `Uploading... ${uploadProgress}%`;
            case 'confirming':
                return 'Finalizing submission...';
            default:
                return 'Submit Track';
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="animate-spin text-[#F51E1E]" size={40} />
                </div>
            </Layout>
        );
    }

    if (!opportunity) return null;

    return (
        <Layout>
            <div className="max-w-6xl mx-auto" data-testid="opportunity-detail-page">
                {/* Back button */}
                <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/opportunities')}
                    className="flex items-center gap-2 text-[#A3A3A3] hover:text-white mb-6 transition-colors"
                    data-testid="back-btn"
                >
                    <ArrowLeft size={20} />
                    <span>Back to opportunities</span>
                </motion.button>

                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Main content - Left */}
                    <div className="lg:col-span-3">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {/* Header */}
                            <div className="mb-6">
                                <Badge 
                                    variant="outline"
                                    className={opportunity.status === 'open' ? 'status-open' : 'status-closed'}
                                >
                                    {opportunity.status}
                                </Badge>
                            </div>

                            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                                {opportunity.title}
                            </h1>

                            {/* Meta info */}
                            <div className="flex flex-wrap gap-4 mb-8 pb-6 border-b border-[#404040]">
                                {opportunity.deadline_text && (
                                    <div className="flex items-center gap-2 text-[#A3A3A3]">
                                        <Calendar size={18} />
                                        <span>Deadline: {opportunity.deadline_text}</span>
                                    </div>
                                )}
                                {opportunity.accepted_file_types?.length > 0 && (
                                    <div className="flex items-center gap-2 text-[#A3A3A3]">
                                        <FileAudio size={18} />
                                        <span>Accepts: {opportunity.accepted_file_types.join(', ')}</span>
                                    </div>
                                )}
                            </div>

                            {/* Needed */}
                            {opportunity.needed && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-bold text-white mb-2">What's Needed</h3>
                                    <p className="text-[#A3A3A3]">{opportunity.needed}</p>
                                </div>
                            )}

                            {/* Description */}
                            {(opportunity.description || opportunity.raw_text) && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-bold text-white mb-2">Description</h3>
                                    <div className="prose prose-invert max-w-none">
                                        <p className="text-[#A3A3A3] whitespace-pre-wrap">
                                            {opportunity.description || opportunity.raw_text}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Reference Links */}
                            {opportunity.reference_links?.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-bold text-white mb-4">Reference Tracks</h3>
                                    <div className="space-y-3">
                                        {opportunity.reference_links.map((link, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-3 p-3 rounded-xl bg-[#262626] border border-[#404040] group"
                                            >
                                                <div className={`
                                                    w-10 h-10 rounded-lg flex items-center justify-center
                                                    ${link.type === 'spotify' ? 'bg-green-500/20 text-green-400' : ''}
                                                    ${link.type === 'youtube' ? 'bg-red-500/20 text-red-400' : ''}
                                                    ${link.type === 'instagram' ? 'bg-pink-500/20 text-pink-400' : ''}
                                                    ${link.type === 'other' ? 'bg-[#404040] text-white' : ''}
                                                `}>
                                                    <ExternalLink size={18} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-white font-medium truncate">
                                                        {link.text || link.href}
                                                    </p>
                                                    <p className="text-xs text-[#A3A3A3]">{link.domain}</p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(link.href);
                                                                toast.success('Link copied to clipboard');
                                                            } catch (err) {
                                                                // Fallback for environments without clipboard access
                                                                const textArea = document.createElement('textarea');
                                                                textArea.value = link.href;
                                                                textArea.style.position = 'fixed';
                                                                textArea.style.left = '-999999px';
                                                                document.body.appendChild(textArea);
                                                                textArea.select();
                                                                try {
                                                                    document.execCommand('copy');
                                                                    toast.success('Link copied to clipboard');
                                                                } catch (e) {
                                                                    toast.error('Could not copy link');
                                                                }
                                                                document.body.removeChild(textArea);
                                                            }
                                                        }}
                                                        className="px-3 py-1.5 text-xs bg-[#404040] hover:bg-[#505050] text-white rounded-lg transition-colors"
                                                        title="Copy link"
                                                    >
                                                        Copy
                                                    </button>
                                                    <a
                                                        href={link.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer nofollow"
                                                        referrerPolicy="no-referrer"
                                                        className="px-3 py-1.5 text-xs bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-lg transition-colors"
                                                    >
                                                        Open
                                                    </a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-[#666] mt-3">
                                        Tip: If a link doesn't open, copy it and paste in a new browser tab
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    </div>

                    {/* Submission form - Right */}
                    <div className="lg:col-span-2">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="sticky top-8"
                        >
                            <div className="bg-[#262626] border border-[#404040] rounded-2xl p-6">
                                {submitted ? (
                                    <div className="text-center py-8">
                                        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                                            <CheckCircle2 className="text-green-400" size={32} />
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-2">Submitted!</h3>
                                        <p className="text-[#A3A3A3] mb-6">
                                            Your track has been submitted. We'll notify you of any updates.
                                        </p>
                                        <Button
                                            onClick={() => navigate('/submissions')}
                                            className="bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-full"
                                            data-testid="view-submissions-btn"
                                        >
                                            View My Submissions
                                        </Button>
                                    </div>
                                ) : opportunity.status === 'closed' ? (
                                    <div className="text-center py-8">
                                        <div className="w-16 h-16 rounded-full bg-[#404040] flex items-center justify-center mx-auto mb-4">
                                            <X className="text-[#A3A3A3]" size={32} />
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-2">Closed</h3>
                                        <p className="text-[#A3A3A3]">
                                            This opportunity is no longer accepting submissions.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <h3 className="text-xl font-bold text-white mb-6">
                                            Submit Your Track
                                        </h3>

                                        <form onSubmit={handleSubmit} className="space-y-5">
                                            {/* File upload */}
                                            <div>
                                                <Label className="text-white mb-2 block">Audio File *</Label>
                                                <div
                                                    onDragEnter={handleDrag}
                                                    onDragLeave={handleDrag}
                                                    onDragOver={handleDrag}
                                                    onDrop={handleDrop}
                                                    onClick={() => fileInputRef.current?.click()}
                                                    data-testid="file-dropzone"
                                                    className={`
                                                        border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
                                                        transition-colors
                                                        ${dragActive 
                                                            ? 'border-[#F51E1E] bg-[#F51E1E]/10' 
                                                            : 'border-[#404040] hover:border-[#F51E1E]/50'
                                                        }
                                                        ${audioFile ? 'bg-[#1a1a1a]' : ''}
                                                    `}
                                                >
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept="audio/*"
                                                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                                                        className="hidden"
                                                        data-testid="file-input"
                                                    />
                                                    
                                                    {audioFile ? (
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-12 h-12 rounded-lg bg-[#F51E1E]/20 flex items-center justify-center">
                                                                <Music className="text-[#F51E1E]" size={24} />
                                                            </div>
                                                            <div className="text-left flex-1 min-w-0">
                                                                <p className="text-white font-medium truncate">{audioFile.name}</p>
                                                                <p className="text-xs text-[#A3A3A3]">
                                                                    {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                                                                </p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAudioFile(null);
                                                                }}
                                                                className="text-[#A3A3A3] hover:text-white"
                                                            >
                                                                <X size={20} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <Upload className="mx-auto text-[#A3A3A3] mb-3" size={32} />
                                                            <p className="text-white font-medium mb-1">
                                                                Drop your track here
                                                            </p>
                                                            <p className="text-sm text-[#A3A3A3]">
                                                                or click to browse
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Track title */}
                                            <div>
                                                <Label htmlFor="trackTitle" className="text-white mb-2 block">
                                                    Track Title
                                                </Label>
                                                <Input
                                                    id="trackTitle"
                                                    value={trackTitle}
                                                    onChange={(e) => setTrackTitle(e.target.value)}
                                                    placeholder="Enter track title"
                                                    data-testid="track-title-input"
                                                    className="h-11 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500"
                                                />
                                            </div>

                                            {/* Notes */}
                                            <div>
                                                <Label htmlFor="notes" className="text-white mb-2 block">
                                                    Notes (optional)
                                                </Label>
                                                <Textarea
                                                    id="notes"
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    placeholder="Any additional information about your submission..."
                                                    rows={3}
                                                    data-testid="notes-input"
                                                    className="bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500 resize-none"
                                                />
                                            </div>

                                            {/* Submit button */}
                                            <div className="space-y-3">
                                                {submitting && (
                                                    <div className="space-y-2">
                                                        <Progress value={uploadProgress} className="h-2" />
                                                        <p className="text-xs text-center text-[#A3A3A3]">
                                                            {getUploadStatusText()}
                                                        </p>
                                                    </div>
                                                )}
                                                <Button
                                                    type="submit"
                                                    disabled={submitting || !audioFile}
                                                    data-testid="submit-btn"
                                                    className="w-full h-12 bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-full font-semibold uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {submitting ? (
                                                        <div className="flex items-center gap-2">
                                                            <Loader2 className="animate-spin" size={20} />
                                                            <span>{getUploadStatusText()}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <CloudUpload size={20} />
                                                            <span>Submit Track</span>
                                                        </div>
                                                    )}
                                                </Button>
                                                <p className="text-xs text-center text-[#666]">
                                                    Max file size: 500MB • Supports WAV, MP3, AIFF
                                                </p>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
